import React from "react"
import useNavigations from "hooks/useNavigations"
import styled, { css } from "styled-components"
import LayerMenu from "./LayerMenu"
import Search from "./search"
import DesktopMenu from "./DesktopMenu"
import MobileMenu from "./MobileMenu"
import { Link } from "gatsby"
import { CmsImage, Dropdown, Container } from "@bw/bits"
import { breakpoints } from "../../../theme"
import useGlobalTheme from "../../../hooks/useGlobalTheme"
import { LocalizedLink } from "gatsby-theme-i18n"

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  position: var(--header-position, fixed);
  top: 0;
  left: 0;
  right: 0;
  z-index: 9;
  padding: 12px 0;
  min-height: 55px;
  color: var(--header-text-color, #fff);
  background-color: var(--header-background-color, transparent);
  transition: background-color 0.25s, color 0.25s;
  font-size: 16px;
  line-height: 1.2;

  .logo-at-rest {
    opacity: 1;
  }
  .logo-when-scrolled {
    opacity: 0;
  }

  &.active {
    box-shadow: 10px 10px 35px rgba(0, 0, 0, 0.05);
    background: var(--header-background-color-scrolled, #fff);
    color: var(--header-text-color-scrolled, #000);

    .logo-at-rest {
      opacity: 0;
    }
    .logo-when-scrolled {
      opacity: 1;
    }
  }
`

const Grid = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const Navigation = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  > * + * {
    margin-left: 20px;
  }
`

const LogoWrapper = styled(LocalizedLink)`
  position: relative;
  display: flex;
  align-items: center;
  color: currentColor;
  position: relative;
  ${props => css`
    width: ${props.$width / (props.$highDensity ? 2 : 1)}px;
    height: ${props.$height / (props.$highDensity ? 2 : 1)}px;
  `}

  > div {
    position: absolute;
    top: 0;
    left: 0;
    transition: opacity 0.25s;
  }
`

const Burger = styled.button`
  display: flex;
  align-items: center;
  justify-content: end;
  justify-self: end;
  width: 28px;
  padding: 0;
  background: none;
  border: 0;
  color: currentColor;
  cursor: pointer;
  ${props =>
    props.hasNoDesktopItems &&
    css`
      @media (min-width: ${breakpoints.medium}px) {
        display: none;
      }
    `}

  > svg {
    > rect {
      transition: width 0.25s ease-in-out;
    }
  }
  &:hover {
    > svg {
      > rect:nth-child(1) {
        width: 20px;
      }
      > rect:nth-child(2) {
        width: 15px;
      }
      > rect:nth-child(3) {
        width: 28px;
      }
    }
  }
`

const StyledLink = styled(Link)`
  text-decoration: none;
  color: currentColor;
  font-weight: 500;
  transition: opacity 0.25s ease-in-out;

  &:hover {
    opacity: 0.5;
  }
`

const Header = ({ pageContext, forceNegative }) => {
  const { burger, shortcuts } = useNavigations()
  const theme = useGlobalTheme()
  const [show, setShow] = React.useState(false)
  const [navBar, setNavBar] = React.useState(false)
  const headerRef = React.useRef(null)

  React.useEffect(() => {
    if (!forceNegative) {
      const changeBackground = () => {
        setNavBar(window.scrollY >= 10)
      }

      changeBackground()
      window.addEventListener("scroll", changeBackground, { passive: true })

      return () => {
        window.removeEventListener("scroll", changeBackground)
      }
    }
  }, [setNavBar, forceNegative])

  React.useEffect(() => {
    document.body.style.overflow = show ? "hidden" : null
  }, [show])

  const logoNegative = theme.logo_negative ? theme.logo_negative : theme.logo

  const headerClassname = ["navbar"]
  if (forceNegative || navBar) {
    headerClassname.push("active")
  }

  return (
    <>
      <HeaderWrapper ref={headerRef} className={headerClassname.join(" ")}>
        <Container>
          <Grid>
            <LogoWrapper
              to="/"
              $highDensity={theme.highDensity}
              $height={theme.logo?.localImage?.childImageSharp?.original?.height || 60}
              $width={theme.logo?.localImage?.childImageSharp?.original?.width || 60}
              className="logo"
            >
              <div className="logo-at-rest">
                <CmsImage
                  src={theme.logo?.localImage?.childImageSharp?.original?.src}
                  alt={`Logo ${process.env.GATSBY_SITE_TITLE}`}
                />
              </div>
              <div className="logo-when-scrolled">
                <CmsImage
                  src={logoNegative?.localImage?.childImageSharp?.original?.src}
                  alt={`Logo ${process.env.GATSBY_SITE_TITLE}`}
                />
              </div>
            </LogoWrapper>
            <Navigation>
              <DesktopMenu menu={shortcuts} />
              <LanguageSelector
                current={pageContext.locale}
                translations={pageContext.translations}
              />
              {theme.feature_search && <Search />}
              {(shortcuts.length > 0 || burger.length > 0) && (
                <Burger
                  hasNoDesktopItems={burger.length === 0}
                  onClick={() => {
                    setShow(true)
                  }}
                >
                  <svg
                    width="26"
                    height="16"
                    viewBox="0 0 26 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect y="7" width="26" height="2" fill="currentColor" />
                    <rect width="26" height="2" fill="currentColor" />
                    <rect y="14" width="26" height="2" fill="currentColor" />
                  </svg>
                </Burger>
              )}
            </Navigation>
          </Grid>
        </Container>
      </HeaderWrapper>
      {(shortcuts.length > 0 || burger.length > 0) && (
        <LayerMenu show={show} setShow={setShow}>
          <MobileMenu shortcuts={shortcuts} menu={burger} />
        </LayerMenu>
      )}
    </>
  )
}

const LanguageSelector = ({ translations, current }) => {
  if (!translations || translations.length <= 1) {
    return null
  }

  if (translations.length === 2) {
    const translation = translations.find(t => t.locale !== current)
    return (
      <StyledLink
        to={translation.slug}
        onClick={() => {
          document.documentElement.setAttribute("lang", translation.locale)
        }}
      >
        {translation.locale.toUpperCase()}
      </StyledLink>
    )
  }

  return (
    <Dropdown
      title={current.toUpperCase()}
      list={translations
        .filter(translation => translation.locale !== current)
        .map(translation => ({
          title: current.toUpperCase(),
          to: translation.slug,
          onClick: () => {
            document.documentElement.setAttribute("lang", translation.locale)
          },
        }))}
    />
  )
}

export { Header }
